import React, { useState, useEffect } from "react";
import EditSheet from "./EditSheet";
import AddTaskSheet from "./AddTaskSheet";
import AddActivitySheet from "./AddActivitySheet";
import CalendarSection from "./CalendarSection";

const ACCENT_MUTED = "#FBF3E0";
const ACCENT_LIGHT = "#F0DFA0";
const ACCENT = "#D4A847";
const CARISSA_CATEGORIES = "Personal,Family,Kids";

const TAG_COLORS = {
  Email: { bg: "#F1F1EF", text: "#787774" },
  Call: { bg: "#DDEDEA", text: "#2B8A3E" },
  Pay: { bg: "#EEE0DA", text: "#8C5B3F" },
  Schedule: { bg: "#EAE4F2", text: "#6940A5" },
  "Sign Up": { bg: "#FBF3DB", text: "#9F6B00" },
};

function TagPills({ tags }) {
  if (!tags || tags.length === 0) return null;
  return tags.map(t => {
    const c = TAG_COLORS[t] || { bg: "var(--cream-hover)", text: "var(--muted)" };
    return (
      <React.Fragment key={t}>
        <span className="meta-dot" />
        <span style={{ fontSize: 11, fontWeight: 700, padding: "1px 7px", borderRadius: 20, background: c.bg, color: c.text }}>{t}</span>
      </React.Fragment>
    );
  });
}

const NAV_TABS = [
  { id: "home", icon: "🏠", label: "Home" },
  { id: "school", icon: "🏫", label: "School & Activities" },
  { id: "links", icon: "🔗", label: "Quick Links" },
  { id: "essential", icon: "🔒", label: "Essential Info" },
];

function formatDate(dateStr) {
  if (!dateStr) return { label: "No due date", soon: false };
  const today = new Date();
  const date = new Date(dateStr + "T00:00:00");
  const diffDays = Math.ceil((date - today) / (1000 * 60 * 60 * 24));
  if (diffDays < 0) return { label: dateStr, soon: true };
  if (diffDays === 0) return { label: "Today", soon: true };
  if (diffDays === 1) return { label: "Tomorrow", soon: true };
  if (diffDays <= 7) return { label: date.toLocaleDateString("en-US", { month: "short", day: "numeric" }), soon: true };
  return { label: date.toLocaleDateString("en-US", { month: "short", day: "numeric" }), soon: false };
}

function formatActivityDate(d) {
  if (!d) return null;
  return new Date(d + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

function formatNoteDate(iso) {
  if (!iso) return null;
  return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

function formatTodayLabel() {
  return new Date().toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
}

function SkeletonRow() {
  return (
    <div style={{ display: "flex", gap: 12, padding: "14px 20px", borderBottom: "1px solid var(--border)", alignItems: "flex-start" }}>
      <div className="skeleton" style={{ width: 40, height: 40, borderRadius: 10, flexShrink: 0 }} />
      <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 6 }}>
        <div className="skeleton skeleton-line-long" style={{ height: 15 }} />
        <div className="skeleton skeleton-line-short" style={{ height: 12 }} />
      </div>
    </div>
  );
}

function SeeAllRow({ label, onTap }) {
  return (
    <div onClick={onTap} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 20px", background: "var(--cream-hover)", borderTop: "1px solid var(--border)", cursor: "pointer" }}>
      <span style={{ fontSize: 13, fontWeight: 700, color: "var(--sage)" }}>{label}</span>
      <span style={{ fontSize: 16, color: "var(--sage)" }}>›</span>
    </div>
  );
}

export default function CarissaDashboard({ onBack, onActivityTap, onTabTap, onSeeAllTasks, onSeeAllReminders, onSeeAllActivities, onNotebookTap, onNoteTap }) {
  const [tab, setTab] = useState("schedule");
  const [tasks, setTasks] = useState([]);
  const [reminders, setReminders] = useState([]);
  const [activities, setActivities] = useState([]);
  const [notebooks, setNotebooks] = useState([]);
  const [pinnedNotes, setPinnedNotes] = useState([]);
  const [loadingTasks, setLoadingTasks] = useState(true);
  const [loadingActivities, setLoadingActivities] = useState(true);
  const [loadingNotes, setLoadingNotes] = useState(true);
  const [editItem, setEditItem] = useState(null);
  const [showAddTask, setShowAddTask] = useState(false);
  const [addTaskType, setAddTaskType] = useState("To Do");
  const [showAddActivity, setShowAddActivity] = useState(false);
  const [showAllTasks, setShowAllTasks] = useState(false);
  const [showAllReminders, setShowAllReminders] = useState(false);

  useEffect(() => {
    Promise.all([
      fetch("/.netlify/functions/notion?type=tasks&exclude=Robert").then(r => r.json()),
      fetch("/.netlify/functions/notion?type=reminders&exclude=Robert").then(r => r.json()),
    ]).then(([td, rd]) => { setTasks(td.items || []); setReminders(rd.items || []); })
      .finally(() => setLoadingTasks(false));
    fetch("/.netlify/functions/notion?type=activities&person=Carissa")
      .then(r => r.json()).then(d => setActivities(d.items || []))
      .finally(() => setLoadingActivities(false));
    Promise.all([
      fetch(`/.netlify/functions/notion?type=notebooks&categories=${encodeURIComponent(CARISSA_CATEGORIES)}`).then(r => r.json()),
      fetch(`/.netlify/functions/notion?type=pinnedNotes&categories=${encodeURIComponent(CARISSA_CATEGORIES)}`).then(r => r.json()),
    ]).then(([nbData, pinData]) => { setNotebooks(nbData.items || []); setPinnedNotes(pinData.items || []); })
      .finally(() => setLoadingNotes(false));
  }, []);

  const handleCheck = async (e, id) => {
    e.stopPropagation();
    setTasks(prev => prev.filter(i => i.id !== id));
    await fetch("/.netlify/functions/notion", { method: "PATCH", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ pageId: id, action: "toggleDone", value: true }) });
  };
  const handleStar = async (e, id, current) => {
    e.stopPropagation();
    setTasks(prev => prev.map(i => i.id === id ? { ...i, isHighPriority: !current } : i));
    await fetch("/.netlify/functions/notion", { method: "PATCH", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ pageId: id, action: "togglePriority", value: !current }) });
  };
  const handleSave = (updated) => { setTasks(prev => prev.map(i => i.id === updated.id ? updated : i)); setEditItem(null); };
  const handleDelete = (id) => { setTasks(prev => prev.filter(i => i.id !== id)); setEditItem(null); };

  const TABS = [{ id: "schedule", label: "Schedule" }, { id: "activities", label: "Activities" }, { id: "notes", label: "Notes" }];
  const pinnedFirst = notebooks.find(n => n.pinned);
  const restNotebooks = notebooks.filter(n => !n.pinned);

  return (
    <>
      <div className="app">
        <header style={{ background: "var(--white)", borderBottom: "1px solid var(--border)", position: "relative", overflow: "hidden" }}>
          <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 4, background: ACCENT }} />
          <div style={{ position: "absolute", top: -30, right: -30, width: 110, height: 110, background: ACCENT_MUTED, borderRadius: "50%", opacity: 0.5 }} />
          <div style={{ position: "relative", display: "flex", alignItems: "center", gap: 14, padding: "52px 20px 0" }}>
            <button onClick={onBack} style={{ width: 34, height: 34, borderRadius: "50%", background: "var(--cream-hover)", border: "none", fontSize: 18, color: "var(--mid)", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "inherit", flexShrink: 0 }}>‹</button>
            <div style={{ width: 52, height: 52, borderRadius: "50%", background: ACCENT_MUTED, border: `2.5px solid ${ACCENT_LIGHT}`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 26, flexShrink: 0 }}>👩</div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontFamily: "'Playfair Display', serif", fontSize: 22, fontWeight: 600, color: "var(--charcoal)", lineHeight: 1.1, position: "relative", display: "inline-block" }}>
                Carissa
                <span style={{ position: "absolute", bottom: -2, left: 0, width: "100%", height: 2, background: ACCENT, borderRadius: 2 }} />
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 5, flexWrap: "wrap" }}>
                <span style={{ fontSize: 14, color: "var(--muted)", fontWeight: 600 }}>My Dashboard</span>
                <span style={{ width: 3, height: 3, borderRadius: "50%", background: "var(--border)" }} />
                <span style={{ fontSize: 13, color: "var(--mid)", fontWeight: 600 }}>{formatTodayLabel()}</span>
              </div>
            </div>
          </div>
          <div style={{ display: "flex", marginTop: 8 }}>
            {TABS.map(t => (
              <button key={t.id} onClick={() => setTab(t.id)} style={{ flex: 1, textAlign: "center", padding: "10px 0", fontSize: 13, fontWeight: 700, cursor: "pointer", background: "none", border: "none", fontFamily: "inherit", borderBottom: `2.5px solid ${tab === t.id ? ACCENT : "transparent"}`, color: tab === t.id ? ACCENT : "var(--muted)" }}>{t.label}</button>
            ))}
          </div>
        </header>

        <div className="scroll-body">
          {tab === "schedule" && (
            <>
              <div className="section-head">
                <span className="section-label">Reminders</span>
                <button onClick={() => { setAddTaskType("Reminder"); setShowAddTask(true); }} style={{ background: "var(--sage)", border: "none", borderRadius: 14, width: 24, height: 24, fontSize: 15, fontWeight: 800, color: "white", cursor: "pointer", fontFamily: "inherit", display: "flex", alignItems: "center", justifyContent: "center", lineHeight: 1 }}>+</button>
              </div>
              <div style={{ background: "var(--white)", borderTop: "1px solid var(--border)" }}>
                {loadingTasks ? <SkeletonRow /> : reminders.length === 0 ? (
                  <div className="empty-state" style={{ padding: "16px 20px" }}><div className="empty-text">No reminders</div></div>
                ) : (showAllReminders ? reminders : reminders.slice(0, 3)).map(item => {
                  const d = formatDate(item.dueDate);
                  return (
                    <div key={item.id} className="task-row" onClick={() => setEditItem(item)}>
                      <div style={{ width: 28, height: 28, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                        <div style={{ width: 9, height: 9, borderRadius: "50%", background: ACCENT }} />
                      </div>
                      <div className="task-content">
                        <div className="task-name">{item.name}</div>
                        <div className="task-sub">
                          <span className={`task-date${d.soon ? " soon" : ""}`}>{d.label}</span>
                          <TagPills tags={item.tag} />
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
              {reminders.length > 3 && (
                <SeeAllRow label={showAllReminders ? "Show less" : `Show all reminders (${reminders.length})`} onTap={() => setShowAllReminders(v => !v)} />
              )}
              <div className="section-divider" />
              <div className="section-head">
                <span className="section-label">Tasks</span>
                <button onClick={() => { setAddTaskType("To Do"); setShowAddTask(true); }} style={{ background: "var(--sage)", border: "none", borderRadius: 14, width: 24, height: 24, fontSize: 15, fontWeight: 800, color: "white", cursor: "pointer", fontFamily: "inherit", display: "flex", alignItems: "center", justifyContent: "center", lineHeight: 1 }}>+</button>
              </div>
              <div style={{ background: "var(--white)", borderTop: "1px solid var(--border)" }}>
                {loadingTasks ? <><SkeletonRow /><SkeletonRow /></> : tasks.length === 0 ? (
                  <div className="empty-state" style={{ padding: "16px 20px" }}><div className="empty-icon">🎉</div><div className="empty-text">All clear!</div></div>
                ) : (showAllTasks ? tasks : tasks.slice(0, 5)).map(item => {
                  const d = formatDate(item.dueDate);
                  return (
                    <div key={item.id} className="task-row" onClick={() => setEditItem(item)}>
                      <button className="task-check" onClick={e => handleCheck(e, item.id)} aria-label="Complete" />
                      <div className="task-content">
                        <div className="task-name">{item.name}</div>
                        <div className="task-sub">
                          <span className={`task-date${d.soon ? " soon" : ""}`}>{d.label}</span>
                          {item.type && item.type !== "To Do" && <><span className="meta-dot" /><span className="type-pill">{item.type}</span></>}
                          <TagPills tags={item.tag} />
                        </div>
                      </div>
                      <button className={`task-star${item.isHighPriority ? " starred" : ""}`} onClick={e => handleStar(e, item.id, item.isHighPriority)} aria-label="Toggle priority">{item.isHighPriority ? "★" : "☆"}</button>
                    </div>
                  );
                })}
              </div>
              {tasks.length > 5 && (
                <SeeAllRow label={showAllTasks ? "Show less" : `Show all tasks (${tasks.length})`} onTap={() => setShowAllTasks(v => !v)} />
              )}
              <div className="section-divider" />
              <CalendarSection view="carissa" />
            </>
          )}

          {tab === "activities" && (
            <>
              <div className="section-head">
                <span className="section-label">Activities</span>
                <button onClick={() => setShowAddActivity(true)} style={{ background: "var(--sage)", border: "none", borderRadius: 14, width: 24, height: 24, fontSize: 15, fontWeight: 800, color: "white", cursor: "pointer", fontFamily: "inherit", display: "flex", alignItems: "center", justifyContent: "center", lineHeight: 1 }}>+</button>
              </div>
              <div style={{ background: "var(--white)", borderTop: "1px solid var(--border)" }}>
                {loadingActivities ? <><SkeletonRow /><SkeletonRow /></> : activities.length === 0 ? (
                  <div className="empty-state" style={{ padding: "16px 20px" }}><div className="empty-text">No active activities</div></div>
                ) : activities.map(act => {
                  const isEmoji = act.icon && !act.icon.startsWith("http");
                  const isImg = act.icon && act.icon.startsWith("http");
                  return (
                    <div key={act.id} onClick={() => onActivityTap?.(act)} style={{ display: "flex", alignItems: "flex-start", gap: 12, padding: "14px 20px", borderBottom: "1px solid var(--border)", cursor: "pointer" }}>
                      <div style={{ width: 42, height: 42, borderRadius: 11, background: ACCENT_MUTED, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20, flexShrink: 0, overflow: "hidden" }}>
                        {isImg ? <img src={act.icon} alt="" style={{ width: 42, height: 42, objectFit: "cover" }} /> : isEmoji ? act.icon : "🏃"}
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 15, fontWeight: 700, color: "var(--charcoal)" }}>{act.name}</div>
                        {(act.seasonStart || act.seasonEnd) && (
                          <div style={{ fontSize: 13, fontWeight: 700, color: ACCENT, marginTop: 3 }}>
                            {act.seasonStart && act.seasonEnd ? `${formatActivityDate(act.seasonStart)} – ${formatActivityDate(act.seasonEnd)}` : act.seasonEnd ? `Ends ${formatActivityDate(act.seasonEnd)}` : `Started ${formatActivityDate(act.seasonStart)}`}
                          </div>
                        )}
                        {act.location && <div style={{ fontSize: 13, color: "var(--muted)", fontWeight: 500, marginTop: 3 }}>📍 {act.location}</div>}
                        {act.schedule && <div style={{ fontSize: 13, color: "var(--mid)", fontWeight: 500, marginTop: 4, lineHeight: 1.5 }}>{act.schedule}</div>}
                      </div>
                      <span style={{ color: "var(--border)", fontSize: 18, flexShrink: 0, marginTop: 2 }}>›</span>
                    </div>
                  );
                })}
              </div>
              <SeeAllRow label="See all activities" onTap={() => onSeeAllActivities?.()} />
            </>
          )}

          {tab === "notes" && (
            <div style={{ padding: "12px 16px", display: "flex", flexDirection: "column", gap: 10 }}>
              {loadingNotes ? <><SkeletonRow /><SkeletonRow /></> : (
                <>
                  {pinnedNotes.length > 0 && (
                    <>
                      <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: "0.08em", textTransform: "uppercase", color: "var(--muted)", padding: "4px 4px 2px" }}>📌 Pinned</div>
                      {pinnedNotes.map(note => (
                        <div key={note.id} onClick={() => onNoteTap?.(note)} style={{ background: "var(--white)", borderRadius: 12, border: "1px solid var(--border)", display: "flex", alignItems: "center", gap: 12, padding: "13px 16px", cursor: "pointer" }}>
                          <div style={{ fontSize: 18 }}>📌</div>
                          <div style={{ flex: 1 }}>
                            <div style={{ fontSize: 15, fontWeight: 700, color: "var(--charcoal)" }}>{note.name}</div>
                            {note.lastEdited && <div style={{ fontSize: 12, color: "var(--muted)", fontWeight: 500, marginTop: 2 }}>Updated {formatNoteDate(note.lastEdited)}</div>}
                          </div>
                          <span style={{ color: "var(--border)", fontSize: 20 }}>›</span>
                        </div>
                      ))}
                    </>
                  )}
                  {pinnedFirst && (
                    <>
                      <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: "0.08em", textTransform: "uppercase", color: "var(--muted)", padding: "4px 4px 2px", marginTop: 4 }}>📁 Pinned Notebook</div>
                      <div onClick={() => onNotebookTap?.(pinnedFirst, CARISSA_CATEGORIES)} style={{ background: "var(--white)", borderRadius: 16, border: "1px solid var(--border)", padding: "16px 18px", cursor: "pointer", position: "relative", overflow: "hidden" }}>
                        <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 3, background: ACCENT }} />
                        <div style={{ fontSize: 24, marginBottom: 8 }}>📁</div>
                        <div style={{ fontFamily: "'Playfair Display', serif", fontSize: 16, fontWeight: 600, color: "var(--charcoal)", marginBottom: 4 }}>{pinnedFirst.name}</div>
                        <span style={{ fontSize: 12, fontWeight: 800, color: ACCENT, background: `${ACCENT}18`, padding: "2px 10px", borderRadius: 20 }}>{pinnedFirst.count} notes</span>
                      </div>
                    </>
                  )}
                  {restNotebooks.length > 0 && (
                    <>
                      <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: "0.08em", textTransform: "uppercase", color: "var(--muted)", padding: "4px 4px 2px", marginTop: 4 }}>Notebooks</div>
                      {restNotebooks.map(nb => (
                        <div key={nb.id} onClick={() => onNotebookTap?.(nb, CARISSA_CATEGORIES)} style={{ background: "var(--white)", borderRadius: 14, border: "1px solid var(--border)", display: "flex", alignItems: "center", gap: 14, padding: "14px 16px", cursor: "pointer" }}>
                          <div style={{ width: 42, height: 42, borderRadius: 11, background: `${ACCENT}18`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20, flexShrink: 0 }}>📒</div>
                          <div style={{ flex: 1 }}>
                            <div style={{ fontSize: 15, fontWeight: 700, color: "var(--charcoal)" }}>{nb.name}</div>
                            <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 4 }}>
                              <span style={{ fontSize: 12, fontWeight: 800, color: ACCENT, background: `${ACCENT}18`, padding: "2px 8px", borderRadius: 20 }}>{nb.count} notes</span>
                              {nb.lastEdited && <span style={{ fontSize: 12, color: "var(--muted)", fontWeight: 600 }}>Updated {formatNoteDate(nb.lastEdited)}</span>}
                            </div>
                          </div>
                          <span style={{ color: "var(--border)", fontSize: 20 }}>›</span>
                        </div>
                      ))}
                    </>
                  )}
                  {pinnedNotes.length === 0 && notebooks.length === 0 && (
                    <div style={{ textAlign: "center", padding: "48px 0", color: "var(--muted)", fontSize: 14 }}>
                      <div style={{ fontSize: 32, marginBottom: 8 }}>📝</div>No notes yet
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </div>

        <nav style={{ position: "fixed", bottom: 0, left: "50%", transform: "translateX(-50%)", width: "100%", maxWidth: "var(--app-max-width)", background: "var(--white)", borderTop: "1px solid var(--border)", display: "flex", padding: "10px 0 20px", zIndex: 100 }}>
          {NAV_TABS.map(t => (
            <div key={t.id} onClick={() => onTabTap?.(t.id)} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 3, cursor: "pointer", padding: "4px 0" }}>
              <div style={{ fontSize: 22 }}>{t.icon}</div>
              <div style={{ fontSize: 10, fontWeight: 700, color: "var(--muted)", textAlign: "center", lineHeight: 1.2 }}>{t.label}</div>
            </div>
          ))}
        </nav>
      </div>

      <EditSheet item={editItem} onClose={() => setEditItem(null)} onSave={handleSave} onDelete={handleDelete} />
      {showAddTask && <AddTaskSheet onClose={() => setShowAddTask(false)} onAdd={item => { if (item.type === "Reminder") setReminders(prev => [item, ...prev]); else setTasks(prev => [item, ...prev]); setShowAddTask(false); }} defaultPerson="Carissa" defaultType={addTaskType} />}
      {showAddActivity && <AddActivitySheet onClose={() => setShowAddActivity(false)} onAdd={item => { setActivities(prev => [item, ...prev]); setShowAddActivity(false); }} defaultPerson="Carissa" lockPerson={true} />}
    </>
  );
}
